import { readFileSync} from 'node:fs';
import { getOrDefault, getOrUndefined } from '../discovery/settings.ts';
import {
    KUBERNETES_API_TOKEN,
    KUBERNETES_MASTER_URL,
    KUBERNETES_ENV_PREFIX,
    NAMESPACE,
    RESOLVE_NOT_READY_ADDRESSES,
    SERVICE_DNS,
    SERVICE_NAME,
    SERVICE_PORT,
    EXPOSE_EXTERNALLY,
    SERVICE_LABEL_NAME,
    SERVICE_LABEL_VALUE,
    POD_LABEL_NAME,
    POD_LABEL_VALUE,
    SERVICE_PER_POD_LABEL_NAME, SERVICE_PER_POD_LABEL_VALUE
} from './KubernetesProperties.ts';
import {type Properties} from '../config/Properties.ts';

export type DiscoveryMode = 'kubernetes-api' | 'dns-lookup';
export type ExposeExternallyMode = 'auto' | 'enabled' | 'disabled';

function readFileContents(fileName: string): string {
    return readFileSync(fileName, 'utf-8').toString();
}


export class KubernetesConfig {
    readonly serviceDns?: string

    readonly serviceName?: string
    readonly serviceLabelName?: string
    readonly serviceLabelValue?: string
    readonly namespace: string
    readonly podLabelName?: string
    readonly podLabelValue?: string
    readonly resolveNotReadyAddresses: boolean
    readonly exposeExternallyMode: ExposeExternallyMode
    readonly servicePerPodLabelName?: string
    readonly servicePerPodLabelValue?: string
    readonly kubernetesMasterUrl: string
    readonly kubernetesApiToken?: string

    readonly servicePort: number
    private readonly fileContentsReader: (fileName: string) => string;

    readonly tokenProvider: () => string;

    constructor(properties: Properties, fileContentsReader: (fileName: string) => string = readFileContents) {
        this.fileContentsReader = fileContentsReader;
        this.serviceDns = getOrDefault(properties, KUBERNETES_ENV_PREFIX, SERVICE_DNS);
        this.serviceName = getOrDefault(properties, KUBERNETES_ENV_PREFIX, SERVICE_NAME);
        this.serviceLabelName = getOrUndefined(properties, KUBERNETES_ENV_PREFIX, SERVICE_LABEL_NAME);
        this.serviceLabelValue = getOrDefault(properties, KUBERNETES_ENV_PREFIX, SERVICE_LABEL_VALUE, "true");
        this.resolveNotReadyAddresses = getOrDefault(properties, KUBERNETES_ENV_PREFIX, RESOLVE_NOT_READY_ADDRESSES, true);
        this.podLabelName = getOrUndefined(properties, KUBERNETES_ENV_PREFIX, POD_LABEL_NAME);
        this.podLabelValue = getOrUndefined(properties, KUBERNETES_ENV_PREFIX, POD_LABEL_VALUE);

        this.exposeExternallyMode = this.getExposeExternallyMode(properties);
        this.servicePerPodLabelName = getOrUndefined(properties, KUBERNETES_ENV_PREFIX, SERVICE_PER_POD_LABEL_NAME);
        this.servicePerPodLabelValue = getOrUndefined(properties, KUBERNETES_ENV_PREFIX, SERVICE_PER_POD_LABEL_VALUE);
        this.kubernetesMasterUrl = getOrDefault(properties, KUBERNETES_ENV_PREFIX, KUBERNETES_MASTER_URL, "https://kubernetes.default.svc");
        this.tokenProvider = this.buildTokenProvider(properties);
        this.servicePort = getOrDefault(properties, KUBERNETES_ENV_PREFIX, SERVICE_PORT, 0);
        this.namespace = this.getNamespaceWithFallbacks(properties);
    }

    private getExposeExternallyMode(properties: Properties): ExposeExternallyMode {
        const exposeExternally: boolean | undefined = getOrUndefined<boolean>(properties, KUBERNETES_ENV_PREFIX, EXPOSE_EXTERNALLY);
        if (exposeExternally === undefined) {
            return 'auto';
        }
        else if (exposeExternally) {
            return 'enabled';
        }
        else {
            return 'disabled';
        }
    }

    private getNamespaceWithFallbacks(properties: Properties): string {
        let namespace: string | undefined = getOrDefault(properties, KUBERNETES_ENV_PREFIX, NAMESPACE);
        if (!namespace) {
            namespace = process.env['KUBERNETES_NAMESPACE'];
        }
        if (!namespace && this.mode === 'kubernetes-api') {
            namespace = this.readNamespace();
        }
        return namespace;
    }

    private buildTokenProvider(properties: {}): () => string {
        const apiToken: string | undefined = getOrUndefined(properties, KUBERNETES_ENV_PREFIX, KUBERNETES_API_TOKEN);
        if (!apiToken && this.mode === 'kubernetes-api') {
            return () => readFileContents('/var/run/secrets/kubernetes.io/serviceaccount/token')
        } else {
            return () => apiToken;
        }
    }

    private readNamespace(): string {
        return this.fileContentsReader('/var/run/secrets/kubernetes.io/serviceaccount/namespace');
    }


    get mode(): DiscoveryMode {
        if (this.serviceDns) {
            return 'dns-lookup';
        }
        return 'kubernetes-api';
    }

    toString() {
        return "KubernetesConfig: {"
            + "service-dns: " + this.serviceDns
            + ", service-name: " + this.serviceName
            + ", service-port: " + this.servicePort
            + ", service-label-name: " + this.serviceLabelName
            + ", service-label-value: " + this.serviceLabelValue
            + ", namespace: " + this.namespace
            + ", pod-label-name: " + this.podLabelName
            + ", pod-label-value: " + this.podLabelValue
            + ", resolve-not-ready-addresses: " + this.resolveNotReadyAddresses
            + ", expose-externally-mode: " + this.exposeExternallyMode
            + ", service-per-pod-label-name: " + this.servicePerPodLabelName
            + ", service-per-pod-label-value: " + this.servicePerPodLabelValue
            + ", kubernetes-master-url: " + this.kubernetesMasterUrl + "}";
    }
}
